import { Button, Empty, Input, Modal, Table, Tooltip } from "antd"
import type { ColumnType } from "antd/es/table"
import type { SorterResult } from "antd/es/table/interface"
import {
  collection,
  getCountFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore"
import { TrashIcon } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router"

import { EMPTY_ARRAY, EMPTY_OBJ } from "../constants"
import { useActiveUserAuthorizationFromContext } from "../contexts/ActiveUserAuthorizationContext"
import { makeConverter } from "../dbUtils"
import { ASSIGNED_COLOR_CLASSNAMES } from "../documents/colors"
import { getErrorMessage } from "../errors"
import { MimeTypeToDisplay } from "../files/mimetype"
import { db } from "../firebaseApp"
import { useDocumentFilters } from "../hooks/useDocumentFilters"
import useErrorPopup from "../hooks/useErrorPopup"
import { useGroupTags } from "../hooks/useGroupTags"
import { deleteUserDocumentsApi } from "../knowledge/api"
import type { AnyUserDocument, KnowledgeItem } from "../knowledge/types"
import { STATE_MAP } from "../pages/UserDocumentPage/state"
import useSearchResults from "../search/useSearchResults"
import Tag from "../tags/Tag"
import type { GroupTag } from "../tags/types"
import { USER_DOCUMENTS_COLLECTION } from "../types/common"
import { QUESTIONNAIRE_SUPPORTED_MIMETYPES } from "../types/mimetype"
import type { UserDocument, UserDocumentState } from "../types/userDocument"
import {
  batchArray,
  getLexicalSorter,
  shorten,
  uniqueByField,
  uniqueByKey,
} from "../utils"
import BulkAssignButton from "./BulkAssignForm"
import BulkReviewButton from "./BulkReviewButton"
import BulkTagButton from "./BulkTagForm"
import CopyToClipboardButton from "./CopyToClipboardButton"
import { dateColumnProps } from "./table/dateColumn"
import useTableScroll from "./table/useTableScroll"
import UnifiedFilterPopover from "./UnifiedKnowledgeItemsFilter"

const _MAX_DOCS = 5000
const _BATCH_SIZE = 30

interface ColumnSortOrder {
  field: string
  order: "ascend" | "descend"
}

const getRowKey = (item: AnyUserDocument) => item.oid

const getDocumentTitle = ({
  title,
  source_kind,
  document_url,
}: UserDocument): string => {
  if (source_kind === "PUBLIC_WEB_PAGE") {
    try {
      const asUrl = new URL(document_url)
      const path = asUrl.pathname === "/" ? "" : asUrl.pathname
      const simpleUrl = asUrl.hostname + path
      if (title) {
        return `${title} (${simpleUrl})`
      }
      return simpleUrl
    } catch {
      return `${title} (${document_url})`
    }
  }

  return title || document_url || "Unknown"
}

const getColumns = (
  isUserDocumentMode: boolean,
  user: { uid: string },
  tags: GroupTag[],
): ColumnType<AnyUserDocument>[] => {
  const columns: ColumnType<AnyUserDocument>[] = []

  if (isUserDocumentMode) {
    columns.push({
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: getLexicalSorter("title"),
      render: (_title, data: UserDocument) => {
        const fileDisplay =
          MimeTypeToDisplay[data.mimetype ?? "UNKNOWN"] ??
          MimeTypeToDisplay.UNKNOWN
        return (
          <div className="flex items-center gap-2">
            <div className="pt-1">
              <Tooltip title={fileDisplay.name} className="h-4">
                {fileDisplay.icon}
              </Tooltip>
            </div>
            <Link to={`/source-documents/${data.oid}`}>
              {shorten(getDocumentTitle(data), 100)}
            </Link>
            <CopyToClipboardButton text={data.document_url} height="16" />
          </div>
        )
      },
    })
  } else {
    columns.push(
      {
        title: "Content",
        key: "content",
        render: (item: KnowledgeItem) => {
          const { question, answer } = item.content

          return (
            <Link to={`/knowledge-items/${item.oid}`}>
              <div>
                <span className="font-semibold">Q: </span>
                {shorten(question.primary, 150)}
              </div>
              <div>
                <span className="font-semibold">A: </span>
                {shorten(`${answer.primary}\n${answer.secondary ?? ""}`, 150)}
              </div>
            </Link>
          )
        },
      },
      {
        title: "Source",
        key: "source",
        defaultSortOrder: "ascend",
        width: 180,
        render: (item: KnowledgeItem) => {
          if (item.source.kind !== "MANUAL") {
            return (
              <Link to={`/source-documents/${item.source.source_oid}`}>
                {item.source.title}
              </Link>
            )
          }

          const creator = item.creator
          const value =
            creator.uid === user.uid ? "Added by you" : creator.email
          return value
        },
      },
    )
  }

  // Common columns added after the mode-specific columns
  columns.push(
    {
      title: "Tags",
      key: "tags",
      className: "max-w-40 overflow-hidden",
      width: 135,
      render: (item: AnyUserDocument) => (
        <div className="flex flex-wrap">
          {Object.keys(item.tags ?? {}).map((oid) => {
            const tagObj = tags.find((t) => t.oid === oid)
            return tagObj ? (
              <Tag key={oid} tag={tagObj} className="mb-1 overflow-hidden" />
            ) : null
          })}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "state",
      key: "state",
      sorter: getLexicalSorter("state"),
      width: 135,
      render: (state: UserDocumentState, item: AnyUserDocument) => {
        // Show REMOVING as soon as the user changes the state.
        const stateInfo =
          STATE_MAP[
            item.removal_requested && state !== "REMOVED" ? "REMOVING" : state
          ] ?? STATE_MAP.UNKNOWN
        const assignedLine = !item.last_assigned_to
          ? ""
          : item.last_assigned_to.uid === user.uid
            ? "You"
            : item.last_assigned_to.email
        return (
          <>
            <Tooltip
              title={
                item.error_type
                  ? getErrorMessage(
                      {
                        error: {
                          error: item.error_type,
                          detail: item.error_detail,
                        },
                      },
                      false,
                    )
                  : stateInfo.tooltip
              }
            >
              <div
                className={
                  "w-fit rounded px-2 py-0.5 text-xs font-bold " +
                  stateInfo.classNames
                }
              >
                {stateInfo.name}
              </div>
            </Tooltip>
            {assignedLine && (
              <div className="mt-2 max-w-36 truncate">
                <Tooltip
                  title={`Assigned to ${assignedLine}`}
                  className={
                    "rounded-md p-1 text-sm font-semibold " +
                    ASSIGNED_COLOR_CLASSNAMES
                  }
                >
                  {assignedLine}
                </Tooltip>
              </div>
            )}
          </>
        )
      },
    },
  )

  if (isUserDocumentMode) {
    columns.push(
      dateColumnProps({
        title: "Added",
        dataIndex: "created_at",
        width: 135,
        sortable: true,
        defaultSortOrder: isUserDocumentMode ? "descend" : null,
      }),
      dateColumnProps({
        title: "Last Modified",
        dataIndex: "last_modified_at",
        width: 135,
        sortable: true,
        defaultSortOrder: isUserDocumentMode ? "descend" : null,
      }),
    )
  } else {
    columns.push(
      dateColumnProps({
        title: "Reviewed On",
        dataIndex: "last_reviewed_at",
        width: 140,
        sortable: true,
        defaultSortOrder: null,
      }),
    )
    columns.push(
      dateColumnProps({
        title: "Updated On",
        dataIndex: "updated_at",
        width: 140,
        sortable: true,
        defaultSortOrder: isUserDocumentMode ? null : "descend",
      }),
    )
  }

  return columns
}

interface Props {
  isUserDocumentMode: boolean
}

const KnowledgeItemsManager = ({
  isUserDocumentMode,
}: Props): React.ReactNode => {
  const { authUser, activeGroupOid, hasPerm } =
    useActiveUserAuthorizationFromContext()

  const [knowledgeItems, setKnowledgeItems] =
    useState<AnyUserDocument[]>(EMPTY_ARRAY)
  const [removing, setRemoving] = useState<Record<string, boolean>>(EMPTY_OBJ)
  const [querySortOrder, setQuerySortOrder] = useState<ColumnSortOrder>()
  const [docsLoading, setDocsLoading] = useState<boolean>(true)
  const [countLoading, setCountLoading] = useState<boolean>(true)

  const [totalDocs, setTotalDocs] = useState<number>(0)
  const { hasSearchQuery, searchResults, setQuery } = useSearchResults(
    isUserDocumentMode ? "SOURCE_DOCUMENT_PIECE" : "KNOWLEDGE_ITEM",
  )
  const { handleSuccess, handleError } = useErrorPopup()
  const [tags, tagsLoading] = useGroupTags(activeGroupOid)

  const documentFiltersResult = useDocumentFilters()
  const { filterState } = documentFiltersResult
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>(EMPTY_ARRAY)

  // An optimization to avoid re-fetching when the number of docs is small.
  const totalDocsRef = useRef<number>(0)

  const loading = countLoading || docsLoading

  useEffect(() => {
    const colRef = collection(db, USER_DOCUMENTS_COLLECTION).withConverter(
      makeConverter<AnyUserDocument>(),
    )

    void (async () => {
      try {
        setCountLoading(true)
        const total = (
          await getCountFromServer(
            query(
              colRef,
              where("group_oid", "==", activeGroupOid),
              where("is_knowledge_item", "==", !isUserDocumentMode),
            ),
          )
        ).data().count
        setTotalDocs(total)
        totalDocsRef.current = total
      } finally {
        setCountLoading(false)
      }
    })()

    const defaultOrderField = isUserDocumentMode ? "created_at" : "updated_at"
    const defaultOrderDirection = "descend"

    const orderField = querySortOrder?.field ?? defaultOrderField
    const orderDirection = querySortOrder?.order ?? defaultOrderDirection

    const allDocsQuery = query(
      colRef,
      where("group_oid", "==", activeGroupOid),
      where("is_knowledge_item", "==", !isUserDocumentMode),
      orderBy(orderField, orderDirection === "ascend" ? "asc" : "desc"),
      limit(_MAX_DOCS),
    )

    setDocsLoading(true)
    return onSnapshot(
      allDocsQuery,
      (querySnapshot) => {
        setDocsLoading(false)
        setKnowledgeItems((prevDocs) => {
          // Reverse so latest changes are at the beginning.
          const nonRemovedChanges = querySnapshot
            .docChanges()
            .filter((change) => change.type !== "removed")
            .map((change) => change.doc.data())
            .reverse()

          return uniqueByField([...nonRemovedChanges, ...prevDocs], "oid")
        })
      },
      (error) => {
        handleError({
          error,
          prefix: "Error loading documents",
        })
        setDocsLoading(false)
      },
    )
  }, [activeGroupOid, isUserDocumentMode, querySortOrder, handleError])

  const unselectAll = useCallback(() => setSelectedRowKeys(EMPTY_ARRAY), [])

  const getNumChildDocs = useCallback(
    async (selectedDocs: AnyUserDocument[]) => {
      const docs = selectedDocs.filter(
        (doc) =>
          doc.is_completed_questionnaire ||
          (doc.mimetype &&
            QUESTIONNAIRE_SUPPORTED_MIMETYPES.includes(doc.mimetype)),
      )
      if (docs.length === 0) return 0

      const batches = batchArray(docs, _BATCH_SIZE)
      let totalChildDocs = 0

      for (const batch of batches) {
        const childDocsQuery = query(
          collection(db, USER_DOCUMENTS_COLLECTION),
          where("group_oid", "==", activeGroupOid),
          where(
            "source.source_oid",
            "in",
            batch.map((doc) => doc.oid),
          ),
        )

        const childDocsSnapshot = await getCountFromServer(childDocsQuery)
        totalChildDocs += childDocsSnapshot.data().count
      }

      return totalChildDocs
    },
    [activeGroupOid],
  )

  const proceedWithDeletion = useCallback(async () => {
    const removingOids = Object.fromEntries(
      selectedRowKeys.map((oid) => [oid, true]),
    )

    setRemoving((prev) => ({ ...prev, ...removingOids }))
    const thingName = isUserDocumentMode ? "document(s)" : "Answer Bank item(s)"
    try {
      await deleteUserDocumentsApi({ oids: selectedRowKeys })
      unselectAll()
      handleSuccess(
        `${selectedRowKeys.length} ${thingName} will be removed in the background`,
      )
    } catch (error) {
      handleError({
        error,
        prefix: `Couldn't remove ${thingName}`,
      })
    } finally {
      setRemoving(EMPTY_OBJ)
    }
  }, [
    handleSuccess,
    handleError,
    selectedRowKeys,
    isUserDocumentMode,
    unselectAll,
  ])

  const bulkDelete = useCallback(async () => {
    const selectedDocs = knowledgeItems.filter((item) =>
      selectedRowKeys.includes(item.oid),
    )
    const childCount = await getNumChildDocs(selectedDocs)

    if (childCount > 0) {
      Modal.confirm({
        title: "Confirm Deletion",
        content: `${childCount} associated Answer bank item${childCount > 1 ? "s" : ""} will be deleted. Are you sure you want to proceed?`,
        okText: "Delete",
        cancelText: "Cancel",
        okButtonProps: { danger: true },
        onOk: proceedWithDeletion,
      })
    } else {
      await proceedWithDeletion()
    }
  }, [selectedRowKeys, knowledgeItems, getNumChildDocs, proceedWithDeletion])

  const [tableContainerRef, tableScrollProp] = useTableScroll()
  const columns = useMemo(
    () => getColumns(isUserDocumentMode, authUser, tags),
    [isUserDocumentMode, authUser, tags],
  )

  const onTableChange = useCallback(
    (
      _pagination: object,
      _filters: object,
      sorter: SorterResult<AnyUserDocument> | SorterResult<AnyUserDocument>[],
    ) => {
      if (!sorter) {
        return
      }

      const canSkipSort =
        totalDocsRef.current !== undefined && totalDocsRef.current < _MAX_DOCS
      if (canSkipSort) {
        // We don't need to change the query sort order.
        return
      }

      let sorterCol = null
      if (Array.isArray(sorter)) {
        sorterCol = sorter[0]
      } else {
        sorterCol = sorter
      }
      if (sorterCol?.field && sorterCol.order) {
        setQuerySortOrder({
          // Always a string for our columns.
          field: sorterCol.field as string,
          order: sorterCol.order,
        })
      } else {
        setQuerySortOrder(undefined)
      }
    },
    [],
  )

  const locale = useMemo(
    () => ({
      emptyText: (
        <Empty
          description={
            loading
              ? "Loading your documents..."
              : hasSearchQuery
                ? `No ${isUserDocumentMode ? "documents" : "items"} match your search`
                : isUserDocumentMode
                  ? `No documents${hasPerm("doc.modify") ? ", add one to get started!" : ""}`
                  : "No items in your Answer Bank, import something to get started!"
          }
        />
      ),
    }),
    [isUserDocumentMode, hasSearchQuery, hasPerm, loading],
  )

  const anyRemoving = Object.values(removing).some((v) => v)

  const rowSelection = useMemo(
    () =>
      hasPerm("doc.modify")
        ? {
            selectedRowKeys,
            onChange: (keys: React.Key[]) =>
              // Cast is safe because we use string keys for table rows.
              setSelectedRowKeys(keys as string[]),
            columnWidth: 48,
            getCheckboxProps: () => ({
              disabled: anyRemoving,
            }),
          }
        : undefined,
    [hasPerm, anyRemoving, selectedRowKeys],
  )

  const filteredKnowledgeItems = useMemo(() => {
    let filtered = knowledgeItems

    if (searchResults) {
      const docsByOid = new Map(knowledgeItems.map((d) => [d.oid, d]))
      if (isUserDocumentMode) {
        filtered = uniqueByKey(
          searchResults,
          (r) => r.document.source_document_oid!,
        )
          .map((r) => docsByOid.get(r.document.source_document_oid!))
          .filter((r) => !!r)
      } else {
        filtered = uniqueByKey(
          searchResults,
          (r) => r.document.knowledge_item_oid!,
        )
          .map((r) => docsByOid.get(r.document.knowledge_item_oid!))
          .filter((r) => !!r)
      }
    }

    if (filterState.creator) {
      filtered = filtered.filter(
        (item) => item.creator.email === filterState.creator,
      )
    }

    if (filterState.source) {
      filtered = filtered.filter(
        (item) =>
          item.is_knowledge_item &&
          item.source.kind === "COMPLETED_QUESTIONNAIRE" &&
          item.source.source_oid === filterState.source,
      )
    }

    if (filterState.assignedTo) {
      filtered = filtered.filter(
        (item) => item.last_assigned_to?.email === filterState.assignedTo,
      )
    }

    if (filterState.tags.length > 0) {
      filtered = filtered.filter((item) =>
        filterState.tags.every((tag) => item.tags && tag in item.tags),
      )
    }

    if (filterState.sourceKind) {
      filtered = filtered.filter(
        (item) => filterState.sourceKind === item.source_kind,
      )
    }

    return filtered
  }, [isUserDocumentMode, searchResults, knowledgeItems, filterState])

  return (
    <div className="flex grow flex-col overflow-y-hidden p-8">
      <div className="mb-2 flex gap-2">
        <Input.Search
          placeholder={
            isUserDocumentMode
              ? "search source documents..."
              : "search answer bank..."
          }
          onChange={(e) => setQuery(e.target.value)}
          onSearch={setQuery}
          name="search"
          className="w-full flex-grow"
        />
        <UnifiedFilterPopover
          tags={tags}
          knowledgeItems={knowledgeItems}
          {...documentFiltersResult}
        />
      </div>
      <div className="m-2 flex items-center justify-start gap-2 font-bold text-gray-600">
        {loading ? (
          <div className="leading-7">Loading...</div>
        ) : (
          <div className="leading-7">
            Showing {filteredKnowledgeItems.length.toLocaleString()} of{" "}
            {totalDocs.toLocaleString()} items
          </div>
        )}
        {selectedRowKeys.length > 0 && (
          <>
            <span> · </span>
            <span>{`${selectedRowKeys.length} selected:`}</span>
            <BulkAssignButton
              oids={selectedRowKeys}
              onFinish={unselectAll}
              isUserDocument={isUserDocumentMode}
            />
            <BulkTagButton
              items={knowledgeItems.filter((item) =>
                selectedRowKeys.includes(item.oid),
              )}
              onFinish={unselectAll}
              tags={tags}
              loading={tagsLoading}
              isUserDocument={isUserDocumentMode}
            />
            <BulkReviewButton oids={selectedRowKeys} onFinish={unselectAll} />
            <Button
              size="small"
              loading={anyRemoving}
              disabled={anyRemoving}
              className="ml-1 flex items-center"
              type="default"
              danger
              onClick={bulkDelete}
              icon={<TrashIcon size={12} />}
            >
              <span className="text-[12px] font-bold">Remove</span>
            </Button>
          </>
        )}
      </div>
      <Table
        ref={tableContainerRef}
        onChange={onTableChange}
        virtual
        size="small"
        className="min-w-[900px] max-w-full grow overflow-y-hidden"
        scroll={tableScrollProp}
        dataSource={loading ? undefined : filteredKnowledgeItems}
        rowKey={getRowKey}
        columns={columns}
        locale={locale}
        loading={loading || tagsLoading}
        pagination={false}
        bordered
        rowSelection={rowSelection}
      />
    </div>
  )
}

export default KnowledgeItemsManager
